import Dexie, { type Table } from 'dexie';
import type {
  Attempt,
  AttemptRollup,
  BoardState,
  CategoryLevel,
  Constant,
  DevicePin,
  ExamRun,
  Formula,
  Item,
  LibraryFile,
  LocalSettings,
  NotebookEntry,
  ProblemTemplate,
  Progress,
  Session,
  StudyRun,
  SyncedPrefs,
} from '@/domain/types';

/**
 * IndexedDB cannot index boolean values, so `deleted` and `pinned` are never
 * indexed — they are filtered in memory. At this data volume (a few thousand
 * records) that costs nothing and keeps the flags honest booleans on the wire.
 */
export class ChemBankDb extends Dexie {
  sessions!: Table<Session, string>;
  items!: Table<Item, string>;
  templates!: Table<ProblemTemplate, string>;
  formulas!: Table<Formula, string>;
  constants!: Table<Constant, string>;
  progress!: Table<Progress, string>;
  attempts!: Table<Attempt, string>;
  rollups!: Table<AttemptRollup, string>;
  notebook!: Table<NotebookEntry, string>;
  files!: Table<LibraryFile, string>;
  prefs!: Table<SyncedPrefs, string>;
  levels!: Table<CategoryLevel, string>;

  // Device-local, never synced.
  local!: Table<LocalSettings, string>;
  pins!: Table<DevicePin, string>;
  exams!: Table<ExamRun, string>;
  /** A card deck or quiz left half-finished, so reopening it carries on. */
  runs!: Table<StudyRun, string>;
  /** The whiteboard as it was left. One row, id 'board'. */
  boards!: Table<BoardState, string>;
  /** Library file bytes held on this device. Only pinned files stay here. */
  blobs!: Table<CachedBlob, string>;

  constructor(name = 'chem-bank') {
    super(name);
    this.version(1).stores({
      sessions: 'id, kind, updatedAt, course',
      items: 'id, sessionId, category, type, difficulty, updatedAt, [sessionId+order]',
      templates: 'id, sessionId, category, updatedAt',
      formulas: 'id, sessionId, topic, updatedAt',
      constants: 'id, sessionId, updatedAt',
      progress: 'id, box, dueAt, updatedAt',
      attempts: 'id, itemId, category, at, updatedAt',
      rollups: 'id, category, day, updatedAt',
      notebook: 'id, itemId, updatedAt',
      files: 'id, name, updatedAt',
      prefs: 'id, updatedAt',
      levels: 'id, category, updatedAt',

      local: 'id',
      pins: 'fileId',
      exams: 'id, startedAt',
    });

    // v2 adds the on-device cache of Library file bytes.
    this.version(2).stores({
      blobs: 'fileId, cachedAt',
    });

    // v3 adds the saved place in a deck or a quiz, and the whiteboard.
    this.version(3).stores({
      runs: 'id, updatedAt',
      boards: 'id',
    });
  }
}

/**
 * A Library file's bytes, held on this device only. Never synced.
 *
 * Stored as a typed array rather than a Blob: typed arrays structured-clone
 * predictably everywhere, while Blob-in-IndexedDB has a patchy history across
 * engines. At a 25 MB cap the difference costs nothing.
 */
export interface CachedBlob {
  fileId: string;
  data: Uint8Array<ArrayBuffer>;
  mime: string;
  cachedAt: number;
  size: number;
}

export const db = new ChemBankDb();

/** Every table whose rows participate in sync, keyed by the name used on the wire. */
export const SYNCED_TABLES = [
  'sessions',
  'items',
  'templates',
  'formulas',
  'constants',
  'progress',
  'attempts',
  'rollups',
  'notebook',
  'files',
  'prefs',
  'levels',
] as const;

export type SyncedTableName = (typeof SYNCED_TABLES)[number];

export function syncedTable(name: SyncedTableName): Table<{ id: string; updatedAt: number }, string> {
  return db[name] as unknown as Table<{ id: string; updatedAt: number }, string>;
}
